import { useCallback } from 'react';
import useHttp from './useHttp';
import { useAuth } from '../context/authContext';

export default function useMailTemplate() {
  const { getAuthHeaders } = useAuth();
  const { sendRequest } = useHttp();

  const getTemplateById = useCallback(
    async templateId =>
      sendRequest({
        url: `/api/v1/mailTemplate/${templateId}`,
        method: 'GET',
        headers: getAuthHeaders(),
      }),
    [getAuthHeaders, sendRequest]
  );

  const updateTemplate = useCallback(
    async (templateId, body) =>
      sendRequest({
        url: `/api/v1/mailTemplate/${templateId}`,
        method: 'PATCH',
        headers: getAuthHeaders(),
        body,
      }),
    [getAuthHeaders, sendRequest]
  );

  // Renders an unsaved editor document inside the branded mail shell, so the
  // preview matches what the sending pipeline produces.
  const previewTemplateDoc = useCallback(
    async template =>
      sendRequest({
        url: '/api/v1/mailTemplate/preview',
        method: 'POST',
        headers: getAuthHeaders(),
        body: { template },
      }),
    [getAuthHeaders, sendRequest]
  );

  // Uploads an editor image as base64 and returns the library entry.
  const uploadAsset = useCallback(
    async ({ fileName, contentType, content }) =>
      sendRequest({
        url: '/api/v1/mailTemplate/asset',
        method: 'POST',
        headers: getAuthHeaders(),
        body: { fileName, contentType, content },
      }),
    [getAuthHeaders, sendRequest]
  );

  // Lists the shared mail image library, newest first.
  const getAssets = useCallback(
    async ({ search } = {}) =>
      sendRequest({
        url: '/api/v1/mailTemplate/asset',
        method: 'GET',
        headers: getAuthHeaders(),
        params: { search },
      }),
    [getAuthHeaders, sendRequest]
  );

  // Deletes a library image; the server refuses while a mail still uses it.
  const deleteAsset = useCallback(
    async (assetId, { force = false } = {}) =>
      sendRequest({
        url: `/api/v1/mailTemplate/asset/${assetId}`,
        method: 'DELETE',
        headers: getAuthHeaders(),
        params: force ? { force: 'true' } : undefined,
      }),
    [getAuthHeaders, sendRequest]
  );

  return {
    getTemplateById,
    updateTemplate,
    previewTemplateDoc,
    uploadAsset,
    getAssets,
    deleteAsset,
  };
}
